import { Button } from '@/components/ui/button';
import { sanitizeHTML } from '@/lib/sanitize-html';
import { CheckCircle2, ExternalLink } from 'lucide-react';
import type { PreboardingFormCompletionConfig } from '@hygge/shared';

type CompletionPreviewProps = PreboardingFormCompletionConfig & {
  /** When true, the feedback button becomes a real external link (public runtime). */
  interactive?: boolean;
};

// Inline approximation of the success page the candidate sees after submitting.
// {{tokens}} render literally here — they're resolved at send time (HW-580).
export function CompletionPreview({
  successTitle,
  closingMessage,
  feedbackLink,
  interactive = false,
}: CompletionPreviewProps) {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <div className="space-y-4 p-6 text-center">
        <div className="flex justify-center">
          <CheckCircle2 className="h-12 w-12 text-hygge" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">
          {successTitle || 'Submission successful!'}
        </h2>
        {closingMessage ? (
          <div
            className="prose prose-sm dark:prose-invert mx-auto max-w-none text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(closingMessage) }}
          />
        ) : (
          <p className="text-sm text-muted-foreground/60">Your closing message will appear here.</p>
        )}
        {feedbackLink.enabled && (
          <div className="pt-2">
            {interactive && feedbackLink.url ? (
              <Button asChild className="gap-1.5">
                <a href={feedbackLink.url} target="_blank" rel="noopener noreferrer">
                  {feedbackLink.label || 'Give feedback'}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </Button>
            ) : (
              <Button disabled className="pointer-events-none gap-1.5">
                {feedbackLink.label || 'Give feedback'}
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
